import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bloodBankAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GROUP_COLORS = { 'A+': '#dc2626', 'A-': '#b91c1c', 'B+': '#2563eb', 'B-': '#1d4ed8', 'AB+': '#7c3aed', 'AB-': '#6d28d9', 'O+': '#059669', 'O-': '#047857' };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default function BloodBankDonors() {
    const navigate = useNavigate();
    const [donors, setDonors] = useState([]);
    const [search, setSearch] = useState('');
    const [bgFilter, setBgFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [showDonate, setShowDonate] = useState(null);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({ full_name: '', name_relation: 'S/O', father_name: '', cnic: '', blood_group: 'A+', gender: 'Male', age: '', phone: '', address: '', donor_type: 'Voluntary', notes: '' });
    const [donateForm, setDonateForm] = useState({ component: 'Whole Blood', volume_ml: 450, collection_date: new Date().toISOString().split('T')[0] });

    const loadDonors = useCallback(async () => {
        try {
            const res = await bloodBankAPI.getDonors({ q: search, blood_group: bgFilter });
            setDonors(res.data);
        } catch { toast.error('Failed to load donors'); }
    }, [search, bgFilter]);

    useEffect(() => { loadDonors(); }, [loadDonors]);

    const handleAdd = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await bloodBankAPI.addDonor(form);
            toast.success('Donor registered');
            setShowAdd(false);
            setForm({ full_name: '', name_relation: 'S/O', father_name: '', cnic: '', blood_group: 'A+', gender: 'Male', age: '', phone: '', address: '', donor_type: 'Voluntary', notes: '' });
            loadDonors();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setLoading(false); }
    };

    const handleDonate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await bloodBankAPI.recordDonation({ donor_id: showDonate.id, blood_group: showDonate.blood_group, ...donateForm });
            toast.success('Donation recorded — bag added to inventory');
            setShowDonate(null);
            loadDonors();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setLoading(false); }
    };

    const canDonate = (donor) => {
        if (!donor.last_donation_date) return true;
        const last = new Date(donor.last_donation_date);
        const diff = Math.floor((new Date() - last) / (1000 * 60 * 60 * 24));
        return diff >= 56;
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate('/blood-bank')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>← Back</button>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>👤 Blood Donors</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Donor registry & donation records</p>
                    </div>
                </div>
                <button onClick={() => setShowAdd(true)}
                    style={{ padding: '9px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    + Register Donor
                </button>
            </div>

            {/* Search */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, CNIC, phone..."
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
                <select value={bgFilter} onChange={e => setBgFilter(e.target.value)}
                    style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: 'white' }}>
                    <option value="">All Groups</option>
                    {BLOOD_GROUPS.map(g => <option key={g}>{g}</option>)}
                </select>
            </div>

            {/* Donors Table */}
            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            {['Donor No.', 'Name', 'Father', 'Blood Group', 'CNIC', 'Phone', 'Last Donation', 'Total', 'Eligible', 'Action'].map(h => (
                                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {donors.length === 0 ? (
                            <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No donors registered</td></tr>
                        ) : donors.map(d => {
                            const eligible = canDonate(d);
                            return (
                                <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontFamily: 'monospace', color: '#7c3aed', fontWeight: 700 }}>{d.donor_number}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{d.full_name}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{d.father_name || '—'}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ fontWeight: 900, fontSize: 15, color: GROUP_COLORS[d.blood_group] }}>{d.blood_group}</span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{d.cnic || '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{d.phone || '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{d.last_donation_date ? new Date(d.last_donation_date).toLocaleDateString('en-PK') : 'Never'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 700, color: '#1e3a5f', textAlign: 'center' }}>{d.total_donations}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ background: eligible ? '#f0fdf4' : '#fef2f2', color: eligible ? '#059669' : '#dc2626', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                                            {eligible ? 'Yes' : 'Wait'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 12px' }}>
                                        {eligible && (
                                            <button onClick={() => { setShowDonate(d); setDonateForm({ component: 'Whole Blood', volume_ml: 450, collection_date: new Date().toISOString().split('T')[0] }); }}
                                                style={{ padding: '4px 10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                🩸 Donate
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Add Donor Modal */}
            {showAdd && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>Register Donor</h3>
                            <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
                        </div>
                        <form onSubmit={handleAdd}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Full Name *</label>
                                    <input required style={inputStyle} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Father / Husband Name</label>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <select value={form.name_relation} onChange={e => setForm(f => ({ ...f, name_relation: e.target.value }))}
                                            style={{ ...inputStyle, width: 90, flex: 'none', background: 'white' }}>
                                            {['S/O', 'D/O', 'W/O', 'B/O', 'F/O', 'M/O'].map(r => <option key={r}>{r}</option>)}
                                        </select>
                                        <input style={{ ...inputStyle, flex: 1 }} value={form.father_name} onChange={e => setForm(f => ({ ...f, father_name: e.target.value }))} placeholder="Guardian name" />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>CNIC / B-Form</label>
                                    <input style={inputStyle} value={form.cnic} onChange={e => setForm(f => ({ ...f, cnic: e.target.value }))} placeholder="42301-1234567-8" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Blood Group *</label>
                                    <select required style={{ ...inputStyle, background: 'white' }} value={form.blood_group} onChange={e => setForm(f => ({ ...f, blood_group: e.target.value }))}>
                                        {BLOOD_GROUPS.map(g => <option key={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Gender</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                                        <option>Male</option><option>Female</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Age</label>
                                    <input type="number" style={inputStyle} value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Phone</label>
                                    <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Donor Type</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={form.donor_type} onChange={e => setForm(f => ({ ...f, donor_type: e.target.value }))}>
                                        <option>Voluntary</option><option>Replacement</option><option>Directed</option>
                                    </select>
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Address</label>
                                    <input style={inputStyle} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button type="submit" disabled={loading}
                                    style={{ flex: 1, padding: '10px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {loading ? 'Saving...' : 'Register Donor'}
                                </button>
                                <button type="button" onClick={() => setShowAdd(false)}
                                    style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Donate Modal */}
            {showDonate && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 400 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', margin: 0 }}>🩸 Record Donation</h3>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{showDonate.full_name} — <strong style={{ color: GROUP_COLORS[showDonate.blood_group] }}>{showDonate.blood_group}</strong></p>
                            </div>
                            <button onClick={() => setShowDonate(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
                        </div>
                        <form onSubmit={handleDonate}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Component</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={donateForm.component} onChange={e => setDonateForm(f => ({ ...f, component: e.target.value }))}>
                                        {['Whole Blood', 'Packed RBCs', 'Platelets', 'FFP', 'Cryoprecipitate'].map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Volume (ml)</label>
                                    <input type="number" style={inputStyle} value={donateForm.volume_ml} onChange={e => setDonateForm(f => ({ ...f, volume_ml: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Collection Date</label>
                                    <input type="date" style={inputStyle} value={donateForm.collection_date} onChange={e => setDonateForm(f => ({ ...f, collection_date: e.target.value }))} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button type="submit" disabled={loading}
                                    style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {loading ? 'Saving...' : '🩸 Record Donation'}
                                </button>
                                <button type="button" onClick={() => setShowDonate(null)}
                                    style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
